import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import './SessionsHome.css';
// Reuse the same chat composer styles used on the existing chat screen
import './SessionChatPage.css';
import MessageInputEditor from '../components/MessageInputEditor';
import ProjectWorkflowButtonsCarousel from '../components/ProjectWorkflowButtonsCarousel';
import ChatMessageContent from '../components/ChatMessageContent';
import { UrlTagProvider } from '../utils/tags';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_NEW_CHAT_HINT = 'Type a message below to create a new session for this project.';

const SessionsHome = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { chatMarkdownEnabled } = useAuth();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]); // { name, mime, size, data }
  const [dragOver, setDragOver] = useState(false);
  const [openingDescription, setOpeningDescription] = useState('');
  const [openingLoading, setOpeningLoading] = useState(true);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId) {
        setOpeningDescription('');
        setOpeningLoading(false);
        return;
      }
      setOpeningLoading(true);
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/get`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ projectId }),
        });
        const json = await resp.json().catch(() => ({}));
        if (cancelled) return;
        const project = json?.data || json?.project || null;
        const textValue = typeof project?.opening_description === 'string'
          ? project.opening_description.trim()
          : '';
        setOpeningDescription(textValue);
      } catch (_) {
        if (!cancelled) setOpeningDescription('');
      } finally {
        if (!cancelled) setOpeningLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('Failed to read file'));
    fr.readAsDataURL(file);
  });

  const addFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const prepared = [];
    for (const f of files) {
      try {
        const dataUrl = await readFileAsDataUrl(f);
        prepared.push({ name: f.name, mime: f.type || 'application/octet-stream', size: f.size, data: dataUrl });
      } catch (_) { /* ignore */ }
    }
    if (prepared.length > 0) setAttachments(prev => [...prev, ...prepared]);
  };

  const removeAttachmentAt = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const handleSend = async (contentArg, sendOptions = {}) => {
    const content = (contentArg ?? text).trim();
    if (!content) return;
    const workflowPromptId = sendOptions?.workflowPromptId && String(sendOptions.workflowPromptId).trim()
      ? String(sendOptions.workflowPromptId).trim()
      : null;
    if (!projectId) {
      navigate('/projects', { replace: true });
      return;
    }
    setLoading(true);
    setError('');
    try {
      // 1) Create session with initial message (API will auto-create request and hidden marker)
      const headers = await getAuthHeaders();
      const createResp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          project_id: projectId,
          name: null,
          message: {
            content,
            files: attachments,
            is_agent: false,
            ...(workflowPromptId ? { workflowPromptId } : {}),
          },
        }),
      });
      const createJson = await createResp.json().catch(() => ({}));
      if (!createResp.ok || !createJson?.success) throw new Error(createJson?.message || createJson?.error || 'Failed to create session');
      const session = createJson.data?.session || createJson.data;

      // Notify nav menu about the new session so it appears immediately
      try {
        const detail = {
          session_id: session?.session_id,
          name: session?.name || null,
          created_at: session?.created_at || new Date().toISOString(),
          modified_at: session?.modified_at || session?.created_at || new Date().toISOString(),
          project_id: projectId,
        };
        window.dispatchEvent(new CustomEvent('cascade:session-created', { detail }));
      } catch (_) { /* no-op */ }

      // Navigate to the new chat
      navigate(`/p/${projectId}/c/${session.session_id}`);
      setText('');
      setAttachments([]);
    } catch (e) {
      setError(e.message || 'Failed to start chat');
    } finally {
      setLoading(false);
    }
  };

  const showCustomOpening = !openingLoading && !!openingDescription;

  return (
    <div className="sessions-home-wrapper">
      <div className="sessions-home-card">
        <h2>Start a new chat</h2>
        {showCustomOpening ? (
          <div className="sessions-home-opening chat-message agent">
            <ChatMessageContent
              content={openingDescription}
              markdown={chatMarkdownEnabled !== false}
            />
          </div>
        ) : (
          <div className="sessions-home-subtext">
            {openingLoading ? 'Loading…' : DEFAULT_NEW_CHAT_HINT}
          </div>
        )}
        {/* Use the same composer UI as the existing chat page for visual parity */}
        <div
          className={`chat-composer${dragOver ? ' drag-over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={async (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer?.files?.length) await addFiles(e.dataTransfer.files); }}
        >
          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={async (e) => { await addFiles(e.target.files); e.target.value = ''; }} />
          {attachments?.length > 0 && (
            <div className="attachments-list">
              {attachments.map((f, idx) => (
                <div key={idx} className="attachment-chip" title={`${f.name} (${f.size} bytes)`}>
                  <i className="fas fa-paperclip" /> <span className="name">{f.name}</span>
                  <button type="button" className="remove" aria-label="Remove" onClick={() => removeAttachmentAt(idx)}>
                    <i className="fas fa-times" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <MessageInputEditor
            value={text}
            onChange={setText}
            onSend={handleSend}
            disabled={loading}
            autoExpandTags={true}
            providers={{ url: UrlTagProvider }}
            footerButtonsLeft={(
              <>
                <button type="button" className="mi-btn" title="Attach files" onClick={() => fileInputRef.current?.click()} disabled={loading}>
                  <i className="fas fa-plus" /> Attach
                </button>
                {attachments?.length > 0 && (
                  <span className="mi-hint">{attachments.length} file{attachments.length !== 1 ? 's' : ''} attached</span>
                )}
              </>
            )}
            footerButtonsRight={(
              <>
                {loading ? (<span className="mi-hint"><i className="fas fa-spinner fa-pulse" /> Starting…</span>) : null}
              </>
            )}
            belowFooter={(
              <ProjectWorkflowButtonsCarousel
                projectId={projectId}
                disabled={loading}
                composerText={text}
                messages={[]}
                onSetComposerText={setText}
                onRunWorkflow={({ content, workflowPromptId }) => handleSend(content, { workflowPromptId })}
              />
            )}
          />
          {error && (
            <div className="error" style={{ marginTop: 8 }}>{error}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionsHome;
